import type { TimeChip } from '@/features/scheduling/availability'
import { formatDayLabel } from '@/features/scheduling/availability'
import { Button, ButtonText } from '@/ui/core/button'
import { Sheet, SheetTitle } from '@/ui/core/sheet'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { View } from 'react-native'

/**
 * The chosen time, and the one action left.
 *
 * A sheet, which is what this app opens for a short self-contained task, and what the bar
 * pinned to the bottom of the screen was already imitating: the thing being confirmed set as a
 * title, then one full-width action. As a sheet it gets the grammar for free — the grabber, the
 * dim, the swipe down — and the screen stops carrying a permanent shelf that only ever holds one
 * thing.
 *
 * The day and the time are the title, and the city and studio are the line under it: when it is,
 * then where. Both halves of each are set with the app's own middle dot, so the title is one fact
 * in two parts rather than two lines competing to be read first.
 *
 * There is no "Pick Another Time" any more. Swiping down and tapping the dimmed page both say
 * it, and a link that repeats the gesture already under the reader's thumb is a third way to do
 * what two things do.
 *
 * A reschedule's note goes under the button, not over it. It is a consequence of the tap rather
 * than something to weigh before making it, and above the action it sat in the beat the eye
 * crosses on its way from the title to the button — a grey sentence in the one place nothing
 * should be. Under it, the sheet reads when, where, act, and then the small print.
 *
 * A failure is a toast, not a line in here. It used to be a line, because a sheet is a `Modal`
 * and the root toast host lives in the window beneath it, so a toast raised over this came up
 * behind the dim. The sheet mounts its own host now, and a toast arrives in front — so a failure
 * can be said the way every other failure in this app is said, and this sheet goes back to
 * holding one fact and one action.
 */

export const ConfirmSheet = ({
	chip,
	studioName,
	geoName,
	now,
	cta,
	note,
	isBooking,
	onConfirm,
	onCancel,
}: {
	/** The chosen time, or nothing — which is what closes the sheet. */
	chip?: TimeChip
	studioName: string
	geoName?: string
	now: Date
	cta: string
	note?: string
	isBooking: boolean
	onConfirm: () => void
	onCancel: () => void
}) => (
	<Sheet open={!!chip} onClose={onCancel}>
		{!!chip && (
			<View className="pt-2">
				<SheetTitle
					glyph="clock"
					line={[geoName, studioName].filter(Boolean).join(' · ')}
				>
					{`${formatDayLabel(chip.date, now)} · ${chip.label}`}
				</SheetTitle>
				{/* A small beat, not a margin: the sheet already reserves the home indicator's inset
				    under this block, so twenty-eight on top of that left the small print floating
				    a long way off the bottom edge once it, rather than the button, was last. */}
				<VStack gap={14} className="px-4 pb-3">
					{/* `lg`, like every other full-width primary in a sheet — Refer's share, the
					    kit's claim, the card sheet's save, the feedback's send. It was set to
					    forty-four as a raw number, matched to the times and the way-out pills on
					    the screen behind this one; but this button does not live on that screen,
					    it lives in a sheet, and its neighbours are the other sheets' actions
					    rather than the chips behind the dim. Through the token, so it cannot
					    drift again without the rest drifting with it. */}
					<Button
						size="lg"
						fullWidth
						isLoading={isBooking}
						disabled={isBooking}
						onPress={onConfirm}
					>
						<ButtonText>{cta}</ButtonText>
					</Button>
					{/* Under the action and centred on it, at the size the app sets its small print:
					    a line about what the tap causes belongs to the button, not to the title. The
					    error stays above it, nearer the button it is about. */}
					{!!note && (
						<Text size="body2" tone="muted" className="text-center">
							{note}
						</Text>
					)}
				</VStack>
			</View>
		)}
	</Sheet>
)
